import React, { useState, useEffect } from 'react';
import { X, Maximize2, Minimize2 } from 'lucide-react';

interface ExpandableModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    headerColor?: string;
    children: React.ReactNode;
}

const ExpandableModal: React.FC<ExpandableModalProps> = ({
    isOpen,
    onClose,
    title,
    subtitle,
    icon,
    headerColor = 'from-emerald-500 to-teal-500',
    children
}) => {
    const [isFullScreen, setIsFullScreen] = useState(true);

    // Handle escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div
                className={`relative bg-white rounded-2xl shadow-2xl flex flex-col transition-all duration-300 ${isFullScreen
                        ? 'w-[95vw] h-[95vh] max-w-7xl'
                        : 'w-[85vw] h-[80vh] max-w-5xl'
                    }`}
            >
                {/* Header */}
                <div className={`bg-gradient-to-r ${headerColor} rounded-t-2xl px-6 py-4 flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                        {icon && <div className="text-white">{icon}</div>}
                        <div>
                            <h2 className="text-white font-semibold text-lg">{title}</h2>
                            {subtitle && <p className="text-white/80 text-sm">{subtitle}</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsFullScreen(!isFullScreen)}
                            className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
                            title={isFullScreen ? 'Reduce size' : 'Maximize'}
                        >
                            {isFullScreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default ExpandableModal;
